import React, { useMemo, useRef, useState } from "react";
import { EditDialog, ExposedFunctions } from "../framework/editDialog";
import { defaultFormikConfig, formikFieldProps } from "../framework/formUtils";
import { Box, TextField, Typography } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import LoopIcon from "@mui/icons-material/Loop";
import { STYLE_CONSTANTS } from "../../framework/theme";
import { useFormik } from "formik";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { PlannedWorkShiftSaveDto } from "../../models/plannedWorkShift/plannedWorkShift";
import { MuiSingleDateTimePicker } from "../framework/muiSingleDateTimePicker";
import { VehicleSelect } from "../transport/components/vehicleSelect";
import { DriverSelect } from "../transport/components/driverSelect";
import { useAppOptionValueEquals } from "../../hooks/useAppOption";
import { OptionRoutesEnabled } from "../../models/common/consts";
import { RouteSelect } from "../framework/select/routeSelect";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import RecurrenceDialog from "./RecurrenceDialog";
import { RecurrenceAction } from "../../models/common/enums";
import { useSavePlannedWorkShiftWithFeedback } from "../../hooks/workShiftPlanning/useSavePlannedWorkShiftWithFeedback";
import { RouteDialog } from "./RouteDialog";
import { RouteSaveDto } from "../../models/route/route";

interface WorkShiftDialogProps {
    isEdit: boolean;
    item: PlannedWorkShiftSaveDto;
    open: boolean;
    onClose: () => void;
    recurrenceAction: RecurrenceAction;
}

export const WorkShiftDialog = ({
    isEdit,
    item,
    open,
    onClose,
    recurrenceAction,
}: WorkShiftDialogProps) => {
    const dialogRef = useRef<ExposedFunctions>();
    const { t } = useTranslation();
    const [routesEnabled] = useAppOptionValueEquals(OptionRoutesEnabled, true);

    const [recurrenceOpen, setRecurrenceOpen] = useState(false);

    const [createRouteOpen, setCreateRouteOpen] = useState(false);
    const newRouteInitial: RouteSaveDto = useMemo(
        () => ({ id: null, rowId: null, name: "", comment: null, isActive: true }),
        []
    );

    const handleAddRouteClick = () => {
        setCreateRouteOpen(true);
    };

    // Recurrence-aware submit handler
    const handleSubmit = useSavePlannedWorkShiftWithFeedback(
        item,
        recurrenceAction,
        () => {
            formik.setSubmitting(false);
            dialogRef.current?.setInitialFormDataFromForm();
            onClose();
        },
        () => formik.setSubmitting(false)
    );

    const formik = useFormik<PlannedWorkShiftSaveDto>({
        ...defaultFormikConfig,
        enableReinitialize: true,
        initialValues: {
            ...item,
            recurrence: item?.recurrence ?? undefined,
        },
        validationSchema: yup.object({
            name: yup.string().max(50),
            startTime: yup.string().required(
                t("validation.fieldIsRequired", {
                    field: t("common.startTime"),
                })
            ),
            endTime: yup.string().required(
                t("validation.fieldIsRequired", {
                    field: t("common.endTime"),
                })
            ),
        }),
        onSubmit: handleSubmit,
    });

    return (
        <>
            <EditDialog
                loading={!item}
                open={open}
                title={isEdit ? t("common.edit") : t("common.addNew")}
                formik={formik}
                onClose={onClose}
                enableDefaultActions
                ref={dialogRef}
                body={
                    <Grid2
                        container
                        direction="column"
                        spacing={STYLE_CONSTANTS.formSpacing}
                        sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                        <Grid2 xs={12}>
                            <TextField
                                id="workshift-label"
                                label={t("common.name")}
                                fullWidth
                                {...formikFieldProps(formik, "label")}
                                value={formik.values?.label ?? ""}
                            />
                        </Grid2>
                        <Grid2 xs={12}>
                            <MuiSingleDateTimePicker
                                id="workshift-start-time"
                                fullWidth
                                required
                                asUtc={false}
                                label={t("common.startTime")}
                                {...formikFieldProps(formik, "startTime")}
                            />
                        </Grid2>
                        <Grid2 xs={12}>
                            <MuiSingleDateTimePicker
                                id="workshift-end-time"
                                fullWidth
                                required
                                asUtc={false}
                                label={t("common.endTime")}
                                {...formikFieldProps(formik, "endTime")}
                            />
                        </Grid2>
                        <Grid2>
                            <VehicleSelect
                                id="workshift-vehicle-select"
                                {...formikFieldProps(formik, "vehicleId")}
                            />
                        </Grid2>
                        <Grid2>
                            <DriverSelect
                                id="workshift-employee-select"
                                {...formikFieldProps(formik, "employeeId")}
                            />
                        </Grid2>
                        {routesEnabled && (
                            <Grid2>
                                <Grid2>
                                    <RouteSelect
                                        id="workshift-route-select"
                                        {...formikFieldProps(formik, "routeId")}
                                    />
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                    >
                                        <Button
                                            size="small"
                                            variant="text"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddRouteClick}
                                        >
                                            {t("route.newRoute")}
                                        </Button>
                                    </Box>
                                </Grid2>
                            </Grid2>
                        )}
                        {!recurrenceAction ||
                        recurrenceAction !== RecurrenceAction.OnlyThis ? (
                            <Grid2>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    id="workshift-open-recurrence"
                                    sx={{ cursor: "pointer", mt: 2 }}
                                    onClick={() => setRecurrenceOpen(true)}
                                >
                                    <LoopIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography color="primary">
                                        {!formik.values.recurrence
                                            ? t(
                                                "workShiftPlanning.recurring.makeRecurring"
                                            )
                                            : t(
                                                "workShiftPlanning.recurring.recurrenceSettings"
                                            )}
                                    </Typography>
                                </Box>
                            </Grid2>
                            ) : null}
                    </Grid2>
                }
            />
            <RecurrenceDialog
                open={recurrenceOpen}
                onClose={() => setRecurrenceOpen(false)}
                formik={formik}
            />

            <RouteDialog
                open={createRouteOpen}
                item={newRouteInitial}
                enableIsActive
                onClose={() => setCreateRouteOpen(false)}
                onSaved={(route) => {
                    void formik.setFieldValue("routeId", route.id);
                    setCreateRouteOpen(false);
                }}
            />

        </>
    );
};
